import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { BrowserStorageService } from '@app/utils/services/browser-storage.service';

const LANG_STORAGE_KEY = 'plp_selected_lang';
const AVAILABLE_LANGS = ['es', 'en', 'pt'] as const;
const DEFAULT_LANG = 'es';

type AvailableLang = typeof AVAILABLE_LANGS[number];

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private transloco = inject(TranslocoService);
  private browserStorage = inject(BrowserStorageService);

  /** Carga y activa el idioma preferido (guardado en localStorage o default). */
  async init(): Promise<void> {
    const lang = this.resolvePreferredLang();
    await firstValueFrom(this.transloco.load(lang));
    this.transloco.setActiveLang(lang);
  }

  /** Cambia el idioma activo y lo persiste. No-op si el idioma no está soportado. */
  async setLanguage(lang: string): Promise<void> {
    const normalized = lang.toLowerCase();
    if (!this.isSupported(normalized)) return;
    await firstValueFrom(this.transloco.load(normalized));
    this.transloco.setActiveLang(normalized);
    this.browserStorage.setLocalStorageItem(LANG_STORAGE_KEY, normalized);
  }

  getActiveLanguage(): string {
    return this.transloco.getActiveLang();
  }

  private resolvePreferredLang(): AvailableLang {
    const stored = this.browserStorage.getLocalStorageItem(LANG_STORAGE_KEY);
    return stored && this.isSupported(stored) ? stored : DEFAULT_LANG;
  }

  private isSupported(lang: string): lang is AvailableLang {
    return (AVAILABLE_LANGS as readonly string[]).includes(lang);
  }
}
